import { useState } from "react";
import { router } from "@inertiajs/react";
import Panel from "@/components/ui/Panel";
import Button, { ButtonAnchor } from "@/components/ui/Button";
import Badge from "@/components/ui/Badge";
import { Input } from "@/components/ui/Field";
import { formatEventDateTime } from "@/utils/formatEventDate";
import type { CheckInCall } from "@/interfaces/check_in_call";

// A check-in call has no venue, so unlike an Event it's shown in the viewer's
// own zone - an organizer in Lisbon and one in California each read the time
// they actually have to be there.
function timeLabel(call: CheckInCall) {
  const zone = new Intl.DateTimeFormat(undefined, { timeZoneName: "short" })
    .formatToParts(new Date(call.starts_at))
    .find((p) => p.type === "timeZoneName")?.value;

  return `${formatEventDateTime(call.starts_at)}${zone ? ` ${zone}` : ""} · ${call.duration_minutes} min`;
}

function CallRow({ call }: { call: CheckInCall }) {
  const [note, setNote] = useState(call.my_note ?? "");
  const answered = !!call.my_status;
  const going = call.my_status === "attending";

  const answer = (status: "attending" | "declined", withNote = note) =>
    router.post(
      `/check-in-calls/${call.id}/rsvp`,
      { status, note: withNote },
      { preserveScroll: true },
    );

  return (
    <div className="border-line/20 flex flex-col gap-3 border-b py-4 first:pt-0 last:border-b-0 last:pb-0">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-body text-ink font-bold">{call.title}</p>
          <p className="text-caption text-ink-muted">{timeLabel(call)}</p>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {going && <Badge tone="success">You're going</Badge>}
          {answered && !going && <Badge tone="neutral">Can't make it</Badge>}
          <Badge tone="brand">{call.attending_count} going</Badge>
        </div>
      </div>

      {call.agenda && (
        <p className="text-body text-ink-muted whitespace-pre-line">
          {call.agenda}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          variant={going ? "secondary" : "primary"}
          onClick={() => answer("attending")}
        >
          {going ? "Still going" : "I'll be there"}
        </Button>

        <Button
          size="sm"
          variant="secondary"
          onClick={() =>
            answered && !going
              ? router.delete(`/check-in-calls/${call.id}/rsvp`, {
                  preserveScroll: true,
                })
              : answer("declined")
          }
        >
          {answered && !going ? "Clear answer" : "Can't make it"}
        </Button>

        {/* The link is here whether or not they RSVP'd - someone who forgot to
            answer still has to be able to join. */}
        {call.zoom_url && (
          <ButtonAnchor
            size="sm"
            variant={call.joinable ? "primary" : "secondary"}
            href={call.zoom_url}
            target="_blank"
            rel="noopener noreferrer"
          >
            {call.joinable ? "Join now" : "Call link"}
          </ButtonAnchor>
        )}
      </div>

      {/* Worth more than the yes/no: what an organizer would have raised on the
          call, from the ones who can't be on it. */}
      {answered && (
        <form
          className="flex flex-wrap items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            answer(call.my_status ?? "attending", note);
          }}
        >
          <div className="min-w-48 flex-1">
            <Input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={
                going
                  ? "Anything you want on the agenda?"
                  : "What should HQ know?"
              }
            />
          </div>
          <Button size="sm" variant="secondary" type="submit">
            {call.my_note ? "Update" : "Send"}
          </Button>
        </form>
      )}
    </div>
  );
}

// HQ runs a call for every organizer at once. It lives on the dashboard rather
// than a page of its own because this is where organizers already are.
export default function CheckInCalls({ calls }: { calls: CheckInCall[] }) {
  if (calls.length === 0) return null;

  return (
    <Panel title={`check-in-calls.ics - ${calls.length}`}>
      <div className="flex flex-col">
        {calls.map((call) => (
          <CallRow key={call.id} call={call} />
        ))}
      </div>
    </Panel>
  );
}
